import React, { useEffect, useContext, useState, useMemo } from 'react';
import { FilterContext } from 'components/FilterBar/FilterProvider';
import KpiContainer from 'components/redesign/KpiContainer';
import * as echarts from 'echarts';
import ReactECharts from 'echarts-for-react';
import { constants } from 'constants/Constants';
import pepsicoChipsIcon from '../../../assets/icons/pepsico-chips-icon.svg';
import pepsicoIcon from '../../../assets/icons/pepsico-icon.svg';
import Select from 'react-select';
import { useLayoutNavigation } from 'hooks/layout-navigation-hook';
import { color } from '../../../theme/Color';
import {
  extractMarketDataForAllAll,
  extractMarketData,
  extractSectorMarketData,
  MapNames,
  MapConfig,
} from '../helper/valueCompare';

interface HeatmapViewProps {
  heatMapData: any;
  isLoading: boolean;
  selectedMetric: any; // Passed from parent
  setSelectedMetric: (m: any) => void;
  metricOptions: any[];
  mode: string;
}

const HeatmapView: React.FC<HeatmapViewProps> = ({
  heatMapData,
  isLoading,
  selectedMetric,
  setSelectedMetric,
  metricOptions,
  mode,
}) => {
  const { selectedSector, selectedMarket } = useContext(FilterContext);
  const [selectedMetricUnit, setSelectedMetricUnit] = useState('');
  const [geoMapData, setGeoMapData] = useState(null);
  const { selectCustomStyles } = useLayoutNavigation();

  useEffect(() => {
    if (!heatMapData) {
      return;
    }
    // Use API response if available; otherwise, fallback to testData.
    const data = heatMapData;
    if (data && data.metrics) {
      // Find the matching metric based on the selected dropdown.
      const matchingMetric = selectedMetric
        ? data.metrics.find(
            (m: any) =>
              m.metricDisplayName.toLowerCase() ===
              selectedMetric.label.toLowerCase()
          )
        : data.metrics[0];

      if (matchingMetric) {
        setSelectedMetricUnit(matchingMetric.metricUnit || '');
        const filteredData = { ...data, metrics: [matchingMetric] };

        // **Scenario 1: All Sectors and All Markets**
        if (selectedSector?.id == 99 && selectedMarket?.id == 999999) {
          setGeoMapData(extractMarketDataForAllAll(filteredData));
        }
        // **Scenario 2: Specific Sector, All Markets**
        else if (selectedSector?.id !== 99 && selectedMarket?.id === 999999) {
          setGeoMapData(extractSectorMarketData(filteredData, selectedSector));
        }
        // **Scenario 3 Specific Sector, Specific Market**
        else if (selectedSector?.id !== 99 && selectedMarket?.id !== 999999) {
          if (!matchingMetric.sectors || matchingMetric.sectors.length === 0) {
            const baseConfig = MAP_CONFIGS[selectedSector.sectorId];
            const baseMapData = baseConfig.getData();
            const fallbackData = baseMapData.features
              .filter((f: any) => {
                return (
                  f.properties &&
                  f.properties.name &&
                  f.properties.name.toLowerCase() ===
                    selectedMarket?.marketName?.toLowerCase()
                );
              })
              .map((f: any) => ({
                name: f.properties.name,
                value: null,
                sectorName: selectedSector?.sectorName,
              }));
            setGeoMapData(fallbackData);
          } else {
            setGeoMapData(extractMarketData(filteredData));
          }
        }
      } else {
        setGeoMapData([]);
        setSelectedMetricUnit('');
      }
    }
  }, [selectedMetric, selectedSector, selectedMarket, heatMapData]);

  const computedMinMax = useMemo(() => {
    if (!geoMapData || !Array.isArray(geoMapData) || geoMapData.length === 0) {
      return { min: 0, max: 1 }; // Ensure min and max are different to prevent uniform color
    }

    const values = geoMapData.map((d) => d.value).filter((v) => v !== null);
    if (values.length === 0) {
      return { min: 0, max: 1 }; // Avoid 0-0 scale issue
    }
    const min = Math.min(...values);
    const max = Math.max(...values);
    return min === max ? { min, max: min + 1 } : { min, max };
  }, [geoMapData]);

  const MAP_CONFIGS: Record<string, MapConfig> = {
    [constants.sectors.Europe]: {
      name: MapNames.europe,
      getData: () => require('../../../assets/maps/Europe_Sector.json'),
    },
    [constants.sectors.LATAM]: {
      name: MapNames.latam,
      getData: () => require('../../../assets/maps/LATAM_Sector.json'),
    },
    [constants.sectors.APAC]: {
      name: MapNames.apac,
      getData: () => require('../../../assets/maps/APAC_Sector.json'),
    },
    [constants.sectors.PFNA]: {
      name: MapNames.pfna,
      getData: () => require('../../../assets/maps/PFNA_Sector.json'),
    },
    [constants.sectors.PBNA]: {
      name: MapNames.pbna,
      getData: () => require('../../../assets/maps/PFNA_Sector.json'),
    },
    [constants.sectors.AMESA]: {
      name: MapNames.amesa,
      getData: () => require('../../../assets/maps/AMESA_Sector.json'),
    },
    Global: {
      name: MapNames.world,
      getData: () => require('../../../assets/maps/World.json'),
    },
    All: {
      name: MapNames.world,
      getData: () => require('../../../assets/maps/World.json'),
    },
  };

  const getMapConfig = (selectedSector?: any, selectedMarket?: any) => {
    // Special case for United States market in PFNA or PBNA sectors
    if (
      (selectedSector?.sectorName === 'PFNA' ||
        selectedSector?.sectorName === 'PBNA') &&
      selectedMarket?.marketName === 'United States'
    ) {
      return {
        name: MapNames.usa,
        getData: () => require('../../../assets/maps/USA_Market.json'),
      };
    }

    // Case 1: Show world map in case Sector and market have ALL selected.
    if (
      !selectedSector ||
      selectedSector.sectorName === 'All' ||
      selectedSector.sectorName === 'Global'
    ) {
      return MAP_CONFIGS['All'];
    }

    // Case 2: Specific sector selected and market is "All"
    if (
      selectedSector &&
      (!selectedMarket ||
        selectedMarket.marketName === 'All' ||
        selectedMarket.id === 999999)
    ) {
      return MAP_CONFIGS[selectedSector.sectorId] || MAP_CONFIGS['All'];
    }

    // Case 3: Specific sector and a specific market selected.
    if (selectedSector && selectedMarket && selectedMarket.id !== 999999) {
      // Get the base map (assumed to be the sector map) for coordinates.
      const baseConfig =
        MAP_CONFIGS[selectedSector.sectorId] || MAP_CONFIGS['All'];
      const baseMapData = baseConfig.getData();

      // Filter the GeoJSON features to keep only those matching the selected market.
      const filteredFeatures = baseMapData.features.filter((f: any) => {
        return (
          f.properties &&
          f.properties.name &&
          f.properties.name.toLowerCase() ===
            selectedMarket.marketName.toLowerCase()
        );
      });

      // Create a new GeoJSON object with only the matching feature.
      const newMapData = { ...baseMapData, features: filteredFeatures };
      return {
        name: selectedMarket.marketName.toLowerCase(),
        getData: () => newMapData,
      };
    }

    // Fallback: world map.
    return MAP_CONFIGS['All'];
  };

  // Usage in your component
  const [currentMapName, setCurrentMapName] = useState<MapNames | null>(null);
  const [isMapLoading, setIsMapLoading] = useState(true);

  const registerMap = async () => {
    try {
      setIsMapLoading(true);
      const config = getMapConfig(selectedSector, selectedMarket);

      // If map is already registered, just update the current map name
      if (echarts.getMap(config.name)) {
        setCurrentMapName(config.name);
        setIsMapLoading(false);
        return;
      }

      // Load and register new map
      const mapData = config.getData();
      echarts.registerMap(config.name, mapData);
      setCurrentMapName(config.name);
    } catch (err) {
      console.error('Failed to register map:', err);
    } finally {
      setIsMapLoading(false);
    }
  };

  useEffect(() => {
    registerMap();
  }, [selectedSector, selectedMarket]);

  const getPositionForMap = () => {
    if (
      selectedSector?.sectorName === 'APAC' &&
      selectedMarket.marketName === 'All'
    ) {
      return '-10%';
    }
    // Handling position specifically for New Zealand
    else if (
      selectedSector?.sectorName === 'APAC' &&
      selectedMarket?.id === 17
    ) {
      return '-70%';
    } else {
      return 'center';
    }
  };

  const isSingleCountry = geoMapData?.length === 1;

  const options = {
    tooltip: {
      trigger: 'item',
      backgroundColor: 'black',
      textStyle: {
        color: 'white',
      },
      formatter: ({ name, data }) => {
        // If no data or missing sector info, show nothing
        if (!data || data.sectorName === undefined) {
          return '';
        }

        // Determine if both dropdowns are "All"
        const showSector =
          selectedSector?.sectorName?.toLowerCase() === 'all' &&
          selectedMarket?.marketName?.toLowerCase() === 'all';

        const formatValue = (value) => {
          if (value === null || value === undefined) {
            return ''; // Return empty string instead of showing null/undefined
          }
          return selectedMetricUnit === '$'
            ? `$${value}`
            : `${value}${selectedMetricUnit}`;
        };

        if (showSector) {
          // For the All/All case, display sector-level tooltip.
          // Only show icons for PBNA and PFNA.
          let iconHtml = '';
          const lowerSector = data.sectorName.toLowerCase();

          if (lowerSector === 'pbna') {
            iconHtml = `<img src="${pepsicoIcon}" style="width: 24px; height: 14px; margin-right: 5px;" />`;
          } else if (lowerSector === 'pfna') {
            iconHtml = `<img src="${pepsicoChipsIcon}" style="width: 24px; height: 14px; margin-right: 5px;" />`;
          }
          return `
            <div style="display: flex; align-items: center;">
              ${iconHtml}
              ${data.sectorName}${formatValue(data.value) ? `: ${formatValue(data.value)}` : ''}
            </div>
          `;
        } else {
          // When a specific sector or market is selected, display only the market name if value is null
          if (data.value === null || data.value === undefined) {
            return `<div>${name}</div>`;
          }
          // When a specific sector or market is selected, display market-level tooltip
          return `
            <div style="display: flex; align-items: center;">
            ${name}: ${formatValue(data.value)}
            </div>
          `;
        }
      },
    },
    visualMap: isSingleCountry
      ? {
          type: 'piecewise',
          show: true,
          orient: 'vertical',
          right: 10,
          bottom: 10,
          itemHeight: 150,
          min: computedMinMax.min,
          max: computedMinMax.max,
          pieces: [
            {
              value: geoMapData[0]?.value,
              color:
                geoMapData[0]?.value !== null &&
                geoMapData[0]?.value !== undefined
                  ? '#669DD7'
                  : '#E0E0E0',
              label: '',
            },
          ],
          text: [''], // No min/max labels
          formatter: (value) =>
            selectedMetricUnit === '$'
              ? `$${value}`
              : `${value}${selectedMetricUnit || ''}`,
          textStyle: {
            fontFamily: 'Inter',
            fontSize: '12px',
            color: mode === 'light' ? `${color._131313}` : `${color._efefef}`,
          },
        }
      : {
          type: 'continuous',
          text: [
            computedMinMax.max === 1 && computedMinMax.min === 0
              ? ''
              : selectedMetricUnit === '$'
                ? `$${computedMinMax.max}`
                : `${computedMinMax.max}${selectedMetricUnit}`,
            computedMinMax.max === 1 && computedMinMax.min === 0
              ? ''
              : selectedMetricUnit === '$'
                ? `$${computedMinMax.min}`
                : `${computedMinMax.min}${selectedMetricUnit}`,
          ],
          min: computedMinMax.min,
          max: computedMinMax.max,
          formatter: (value) => {
            return computedMinMax.max === 1 && computedMinMax.min === 0
              ? ''
              : selectedMetricUnit === '$'
                ? `$${value}`
                : `${value}${selectedMetricUnit}`;
          },
          textStyle: {
            fontFamily: 'Inter',
            fontSize: '12px',
            color: mode === 'light' ? `${color._131313}` : `${color._efefef}`,
          },
          calculable: false,
          hoverLink: false,
          right: 10,
          bottom: 10,
          inRange: {
            // Color gradient as per Figma design
            // Low value (light blue) to High value (darker blue)
            color: ['#E6EFF8', '#669DD7'],
          },
        },
    series: [
      {
        type: 'map',
        map: currentMapName,
        left: getPositionForMap(),
        roam: false,
        zoom:
          selectedSector?.sectorName === 'APAC' && selectedMarket?.id === 17
            ? 2
            : 1,
        selectedMode: false,
        emphasis: {
          label: { show: false },
          itemStyle: {
            areaColor: 'inherit',
          },
        },
        select: {
          label: { show: false },
          itemStyle: {
            areaColor: 'inherit', // Prevents yellow highlight when clicked
          },
        },
        data: geoMapData,
      },
    ],
  };

  return (
    <KpiContainer title="Metrics" isLoading={isLoading}>
      <div style={{ marginBottom: '16px', display: 'flex' }}>
        <Select
          options={metricOptions}
          isSearchable={false}
          styles={{
            ...selectCustomStyles,
            control: (provided) => ({
              ...selectCustomStyles.control(provided),
              width: '200px',
            }),
          }}
          value={selectedMetric}
          data-testid="metric-option-dropdown"
          onChange={(selected) => {
            setSelectedMetric(selected);
          }}
        />
      </div>
      <div style={{ width: '100%', height: '500px' }}>
        {isMapLoading ? (
          <div>Loading map...</div>
        ) : (
          <ReactECharts
            option={options}
            style={{ width: '100%', height: '100%' }}
          />
        )}
      </div>
    </KpiContainer>
  );
};

export default HeatmapView;
